// ═══════════════════════════════════════════════════════════════════════
// En-tête général de SOGAS RH (maquette du chef de projet, variante B validée le 3 octobre 2026).
//   bande vert SOGAS foncé : emblème qui déborde (petite courbe), « SOGAS RH » ;
//   menus regroupés intégrés à la bande (un groupe d'une seule entrée devient un lien direct) ;
//   droite : notifications, « Prénom NOM · matricule » (menu du compte), prise = déconnexion.
// Les entrées visibles sont calculées exactement comme avant : rôle, puis permissions effectives.
// Sur mobile : en-tête compact et tiroir de navigation.
// 4 octobre 2026 : un prestataire externe de l'IPM (nature 'EXTERNE') n'a que l'IPM (et Équipe IPM
// s'il est Gestionnaire), la cloche et la déconnexion. « Équipe IPM » : DRH, RH principal,
// administrateur, ou supervision IPM.
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Users, Clock3, CalendarDays, CalendarCheck2, Wallet, HandCoins, HeartPulse, PiggyBank,
  FileStack, Calculator, Landmark, ScrollText, Settings, Home, ChevronDown, Menu, X, LogOut, UserRound, KeyRound, Unplug, UserCog,
  UsersRound, Inbox, CalendarRange, Hourglass, Smartphone,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import { EnteteGlobaleContext } from './enteteGlobale';
// Chantier dispositifs sociaux (équipe WEB) : entrée « Dispositifs sociaux » alimentée par /api/dispositifs/socle/moi/espaces.
import { useEntreeDispositifs, ajouterEntreeDispositifs } from '../pages/dispositifs/espaces';
import '../design/sogas-ui.css';
// Suivi « prochain acteur » : entrée « Dossiers en attente » (droit voir_dossiers_en_attente ou rôle RH global).
import { peutVoirDossiersEnAttente } from './suivi/SuiviDemande';

// ─── Entrées de navigation par rôle (inchangées) ──────────────────────
const ITEMS_NAV = [
  { path: '/dashboard', label: 'Tableau de bord', Ico: LayoutDashboard, groupe: 'accueil',
    roles: ['admin', 'directeur_general', 'directeur_adjoint', 'drh', 'rh_principal', 'assistant_rh', 'prepose_regional', 'responsable_site'] },
  { path: '/employes', Ico: Users, groupe: 'employes',
    roles: ['admin', 'directeur_general', 'directeur_adjoint', 'drh', 'rh_principal', 'assistant_rh', 'prepose_regional', 'responsable_site', 'chef_departement', 'chef_service', 'chef_equipe'],
    labelParRole: { prepose_regional: 'Mon site', responsable_site: 'Mon site', chef_departement: 'Mon département', chef_service: 'Mon service', chef_equipe: 'Mon équipe' },
    labelDefaut: 'Employés' },
  { path: '/pointage', Ico: Clock3, groupe: 'temps',
    roles: ['admin', 'directeur_general', 'directeur_adjoint', 'drh', 'rh_principal', 'assistant_rh', 'prepose_regional', 'responsable_site', 'chef_departement', 'chef_service', 'chef_equipe'],
    labelParRole: { chef_departement: 'Appel nominal', chef_service: 'Appel nominal', chef_equipe: 'Appel nominal' },
    labelDefaut: 'Pointage' },
  // Chantier temps (agent I) : entrée du menu « Temps » (tableau de bord, à traiter, journées, clôture,
  // réglages, revue, qualité) ; aussi visible par les permissions effectives (PERMISSIONS_SUIVI_TEMPS).
  { path: '/temps', label: 'Suivi du temps', Ico: CalendarDays, groupe: 'temps',
    roles: ['admin', 'drh', 'rh_principal', 'assistant_rh', 'prepose_regional', 'responsable_site', 'chef_departement'] },
  { path: '/conges', Ico: CalendarCheck2, groupe: 'temps',
    roles: ['admin', 'directeur_general', 'directeur_adjoint', 'drh', 'rh_principal', 'assistant_rh', 'prepose_regional', 'responsable_site', 'chef_departement', 'chef_service', 'chef_equipe', 'employe'],
    labelParRole: { employe: 'Mes congés', chef_service: 'Congés de l\'équipe', chef_equipe: 'Congés de l\'équipe', chef_departement: 'Congés du département', prepose_regional: 'Congés du site', responsable_site: 'Congés du site' },
    labelDefaut: 'Congés' },
  // Moteur Congés (Lot 3) : rôles RH et admin, ou permissions effectives PERMISSIONS_DROITS_CONGES (voir plus bas).
  { path: '/conges/droits', label: 'Droits à congé', Ico: CalendarRange, groupe: 'temps', roles: ['admin', 'drh', 'rh_principal', 'assistant_rh'] },
  { path: '/prets-sogas', label: 'Prêts SOGAS', Ico: HandCoins, groupe: 'avantages',
    roles: ['admin', 'drh', 'rh_principal', 'directeur_general', 'directeur_adjoint', 'assistant_rh', 'employe', 'prepose_regional', 'responsable_site', 'chef_departement', 'chef_service', 'chef_equipe'] },
  { path: '/dettes', label: 'Prêts et retenues', Ico: Wallet, groupe: 'avantages',
    roles: ['admin', 'drh', 'rh_principal', 'directeur_general', 'directeur_adjoint', 'assistant_rh', 'agent_oeuvre_sociale', 'agent_ipm'] },
  // IPM : visible uniquement avec une habilitation IPM explicite (voir entreesVisibles).
  { path: '/ipm', label: 'IPM', Ico: HeartPulse, groupe: 'avantages', roles: [] },
  // Équipe IPM : administration des accès (DRH, RH principal, administrateur) ou supervision IPM.
  { path: '/ipm/equipe', label: 'Équipe IPM', Ico: UserCog, groupe: 'avantages', roles: [] },
  { path: '/provisions', label: 'Provision retraite', Ico: PiggyBank, groupe: 'avantages',
    roles: ['admin', 'drh', 'rh_principal', 'directeur_general', 'directeur_adjoint', 'assistant_rh'] },
  // RH-PAIE-1 : sans 'admin' — la donnée salariale reste de toute façon filtrée côté serveur.
  { path: '/dossiers-salariaux/qualite', label: 'Dossiers salariaux', Ico: FileStack, groupe: 'paie',
    roles: ['drh', 'rh_principal', 'assistant_rh'] },
  // RH-PAIE-3 : rôles par défaut, ou permission peut_voir_preparation_paie (voir plus bas).
  { path: '/paie/preparation', label: 'Préparation de la paie', Ico: Calculator, groupe: 'paie',
    roles: ['drh', 'rh_principal', 'assistant_rh'] },
  // Dossiers en attente (prochain acteur) : visibilité calculée par peutVoirDossiersEnAttente (voir entreesVisibles).
  { path: '/dossiers-en-attente', label: 'Dossiers en attente', Ico: Hourglass, groupe: 'pilotage', roles: [] },
  { path: '/gouvernance', label: 'Gouvernance RH', Ico: Landmark, groupe: 'pilotage', roles: ['drh', 'rh_principal'] },
  { path: '/audit', label: 'Journal d\'activité', Ico: ScrollText, groupe: 'pilotage', roles: ['rh_principal', 'drh'] },
  { path: '/parametres', label: 'Paramètres', Ico: Settings, groupe: 'pilotage', roles: ['admin', 'drh', 'rh_principal'] },
  // Mises à jour mobiles (0077) : seulement avec l'habilitation gerer_versions_mobile (aucun rôle ne l'ouvre).
  { path: '/parametres/application-mobile', label: 'Application mobile', Ico: Smartphone, groupe: 'pilotage', roles: [] },
];

const GROUPES = [
  { cle: 'accueil', libelle: 'Tableau de bord' },
  { cle: 'employes', libelle: 'Employés' },
  { cle: 'temps', libelle: 'Temps' },
  { cle: 'avantages', libelle: 'Prêts et avantages' },
  { cle: 'paie', libelle: 'Paie' },
  { cle: 'pilotage', libelle: 'Pilotage' },
];

const LIBELLES_ROLE = {
  admin: 'Administrateur', directeur_general: 'Directeur général', directeur_adjoint: 'Directeur adjoint',
  drh: 'Directeur des ressources humaines', rh_principal: 'RH principal', assistant_rh: 'Assistant RH',
  prepose_regional: 'Préposé régional', responsable_site: 'Responsable de site', chef_departement: 'Chef de département',
  chef_service: 'Chef de service', chef_equipe: 'Chef d\'équipe', comptable: 'Comptable', agent_ipm: 'Agent IPM',
  agent_oeuvre_sociale: 'Agent des œuvres sociales', employe: 'Salarié', journalier: 'Journalier',
  intervenant_externe: 'Prestataire externe IPM',
};
const ROLES_EQUIPE_IPM = ['drh', 'rh_principal', 'admin'];

/** Libellé affiché sous le nom : pour un prestataire externe, sa fonction IPM. */
function libelleRoleDe(employe) {
  if (employe?.nature === 'EXTERNE') {
    const f = employe.fonction_ipm && employe.fonction_ipm.fonction;
    const fonction = f === 'GESTIONNAIRE' ? 'Gestionnaire IPM' : f === 'AGENT' ? 'Agent IPM' : null;
    return fonction ? `${fonction} · prestataire externe` : LIBELLES_ROLE.intervenant_externe;
  }
  return LIBELLES_ROLE[employe?.role] || 'Utilisateur';
}

// Permissions effectives (renvoyées par le serveur) qui ouvrent le suivi du temps, quel que soit le rôle.
const PERMISSIONS_SUIVI_TEMPS = ['traiter_anomalies_temps', 'peut_voir_temps_site', 'peut_cloturer_periode_temps', 'peut_exporter_temps',
  'rouvrir_periode_temps', 'parametrer_temps', 'valider_parametres_temps', 'gerer_bornes'];
// Permissions effectives qui ouvrent « Droits à congé » (Lot 3), quel que soit le rôle.
const PERMISSIONS_DROITS_CONGES = ['consulter_droits_conges', 'reprendre_donnees_conges', 'regulariser_conges'];
const ROLES_RH_LAYOUT = ['admin', 'drh', 'rh_principal', 'assistant_rh', 'directeur_general', 'directeur_adjoint'];
const ROLES_EMPLOYE_LIKE = ['employe', 'journalier'];

/** Entrées visibles pour l'utilisateur — même calcul qu'avant la refonte. */
function entreesVisibles(employe) {
  const role = employe?.role || 'employe';
  const permsEff = employe?.permissions_effectives || {};
  const hasPretsSogasAcces = ROLES_RH_LAYOUT.includes(role) || (
    permsEff.peut_soumettre_pret_sogas === true || permsEff.peut_approuver_pret_sogas === true
    || permsEff.peut_traiter_pret_sogas === true || permsEff.peut_debloquer_pret_sogas === true
    || permsEff.peut_voir_tous_prets_sogas === true
  );
  const hasPreparationPaieAcces = ['drh', 'rh_principal', 'assistant_rh'].includes(role) || permsEff.peut_voir_preparation_paie === true;
  // Socle IPM : aucune entrée IPM par le rôle, seulement par habilitation IPM attribuée explicitement.
  const hasIpmAcces = ['ipm_gestion', 'ipm_supervision', 'ipm_controle_rh', 'ipm_controle_paie', 'ipm_controle_financier', 'ipm_commission', 'ipm_donnees_medicales']
    .some((k) => permsEff[k] === true);
  const hasEquipeIpm = permsEff.ipm_supervision === true || (employe?.nature !== 'EXTERNE' && ROLES_EQUIPE_IPM.includes(role));
  const entreeEquipeIpm = { path: '/ipm/equipe', label: 'Équipe IPM', Ico: UserCog };

  // Prestataire externe : l'IPM seulement (jamais d'accueil salarié, de congés ni de prêts).
  if (employe?.nature === 'EXTERNE') {
    return [{ path: '/ipm', label: 'IPM', Ico: HeartPulse }, ...(hasEquipeIpm ? [entreeEquipeIpm] : [])];
  }

  if (ROLES_EMPLOYE_LIKE.includes(role)) {
    const items = [
      { path: '/accueil-employe', label: 'Accueil', Ico: Home },
      { path: '/conges', label: 'Mes congés', Ico: CalendarCheck2 },
      { path: '/prets-sogas', label: 'Prêts SOGAS', Ico: HandCoins },
      { path: '/mes-prets', label: 'Mes prêts', Ico: Wallet },
    ];
    if (permsEff.voir_subordonnes) items.splice(1, 0, { path: '/employes', label: 'Mon équipe', Ico: Users });
    if (permsEff.scanner_qr_code || permsEff.appel_nominal) items.splice(2, 0, { path: '/pointage', label: 'Pointage', Ico: Clock3 });
    if (PERMISSIONS_SUIVI_TEMPS.some((k) => permsEff[k] === true)) items.splice(2, 0, { path: '/temps', label: 'Suivi du temps', Ico: CalendarDays });
    if (PERMISSIONS_DROITS_CONGES.some((k) => permsEff[k] === true)) items.push({ path: '/conges/droits', label: 'Droits à congé', Ico: CalendarRange });
    if (hasIpmAcces) items.push({ path: '/ipm', label: 'IPM', Ico: HeartPulse });
    if (hasEquipeIpm) items.push(entreeEquipeIpm);
    if (Object.keys(permsEff).some((k) => k.startsWith('config_') && permsEff[k] === true)) items.push({ path: '/parametres', label: 'Paramètres', Ico: Settings });
    if (peutVoirDossiersEnAttente(employe)) items.push({ path: '/dossiers-en-attente', label: 'Dossiers en attente', Ico: Hourglass });
    if (permsEff.gerer_versions_mobile === true) items.push({ path: '/parametres/application-mobile', label: 'Application mobile', Ico: Smartphone });
    return hasPretsSogasAcces ? items : items.filter((i) => i.path !== '/prets-sogas');
  }
  if (role === 'comptable') {
    return [
      { path: '/dashboard', label: 'Tableau de bord', Ico: LayoutDashboard },
      { path: '/dettes', label: 'Prêts et retenues', Ico: Wallet, groupe: 'avantages' },
      ...(hasIpmAcces ? [{ path: '/ipm', label: 'IPM', Ico: HeartPulse, groupe: 'avantages' }] : []),
      ...(hasEquipeIpm ? [{ ...entreeEquipeIpm, groupe: 'avantages' }] : []),
      { path: '/prets-sogas', label: 'Prêts SOGAS', Ico: HandCoins, groupe: 'avantages' },
      ...(peutVoirDossiersEnAttente(employe) ? [{ path: '/dossiers-en-attente', label: 'Dossiers en attente', Ico: Hourglass, groupe: 'pilotage' }] : []),
    ];
  }
  return ITEMS_NAV
    .filter((item) => {
      if (item.path === '/paie/preparation') return hasPreparationPaieAcces;
      if (item.path === '/ipm') return hasIpmAcces;
      if (item.path === '/ipm/equipe') return hasEquipeIpm;
      if (item.path === '/dossiers-en-attente') return peutVoirDossiersEnAttente(employe);
      if (item.path === '/parametres/application-mobile') return permsEff.gerer_versions_mobile === true;
      if (item.path === '/temps' && PERMISSIONS_SUIVI_TEMPS.some((k) => permsEff[k] === true)) return true;
      if (item.path === '/conges/droits' && PERMISSIONS_DROITS_CONGES.some((k) => permsEff[k] === true)) return true;
      // Centre de configuration : aussi pour les préparateurs, valideurs et lecteurs habilités (IAM).
      if (item.path === '/parametres' && Object.keys(permsEff).some((k) => k.startsWith('config_') && permsEff[k] === true)) return true;
      if (!item.roles.includes(role)) return false;
      if (item.path === '/prets-sogas' && !hasPretsSogasAcces) return false;
      return true;
    })
    .map((item) => ({ ...item, label: (item.labelParRole && item.labelParRole[role]) || item.labelDefaut || item.label }));
}

/**
 * Chantier temps (agent H) : « Mon équipe » et « À traiter (n) », selon les droits EFFECTIFS renvoyés
 * par le serveur (/api/temps/equipe/droits : permissions hiérarchiques ou délégation active),
 * jamais selon une liste de rôles. Ajoutées au groupe « Temps ».
 */
function ajouterEntreesEquipe(items, droits, employe) {
  if (!droits || employe?.nature === 'EXTERNE') return items;
  const ajout = [];
  if (droits.mon_equipe) {
    const doublon = items.some((i) => i.label === 'Mon équipe');
    ajout.push({ path: '/temps/equipe', label: doublon ? 'Mon équipe aujourd\'hui' : 'Mon équipe', Ico: UsersRound, groupe: 'temps' });
  }
  if (droits.a_traiter) {
    const n = droits.compteur_a_traiter || 0;
    ajout.push({ path: '/temps/a-traiter', label: n ? `À traiter (${n})` : 'À traiter', Ico: Inbox, groupe: 'temps' });
  }
  if (!ajout.length) return items;
  // Menu à plat (salarié encadrant) : juste après l'accueil ; sinon dans le groupe « Temps ».
  if (items.every((i) => !i.groupe)) return [items[0], ...ajout.map((i) => ({ ...i, groupe: undefined })), ...items.slice(1)].filter(Boolean);
  return [...items, ...ajout];
}

/** Droits du responsable (menu) : relus à chaque affichage et après une décision (« sogas:a-traiter »). */
function useDroitsEquipeMenu(actif) {
  const [droits, setDroits] = useState(null);
  useEffect(() => {
    if (!actif) return undefined;
    let vivant = true;
    const charger = () => axios.get('/api/temps/equipe/droits')
      .then((r) => { if (vivant) setDroits(r.data && r.data.data); })
      .catch(() => { if (vivant) setDroits(null); });
    charger();
    window.addEventListener('sogas:a-traiter', charger);
    return () => { vivant = false; window.removeEventListener('sogas:a-traiter', charger); };
  }, [actif]);
  return droits;
}

/** Regroupe les entrées : un groupe d'une seule entrée devient un lien direct. */
function regrouper(items) {
  const sansGroupe = items.filter((i) => !i.groupe);
  if (sansGroupe.length === items.length) return items.map((i) => ({ lien: i }));
  const blocs = [];
  GROUPES.forEach((g) => {
    const membres = items.filter((i) => i.groupe === g.cle);
    if (membres.length === 1) blocs.push({ lien: membres[0] });
    else if (membres.length > 1) blocs.push({ groupe: g, membres });
  });
  sansGroupe.forEach((i) => blocs.unshift({ lien: i }));
  return blocs;
}

function useFermetureExterieure(ouvert, fermer) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ouvert) return undefined;
    const clic = (e) => { if (ref.current && !ref.current.contains(e.target)) fermer(); };
    const echap = (e) => { if (e.key === 'Escape') fermer(); };
    document.addEventListener('mousedown', clic);
    document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', clic); document.removeEventListener('keydown', echap); };
  }, [ouvert, fermer]);
  return ref;
}

function MenuGroupe({ groupe, membres, actif, estActif }) {
  const [ouvert, setOuvert] = useState(false);
  const ref = useFermetureExterieure(ouvert, () => setOuvert(false));
  return (
    <div className="sg-nav-groupe" ref={ref}>
      <button type="button" className={`sg-nav-lien${actif ? ' sg-nav-actif' : ''}`} aria-haspopup="menu" aria-expanded={ouvert}
        onClick={() => setOuvert((o) => !o)}>
        {groupe.libelle}<ChevronDown size={14} aria-hidden="true" />
      </button>
      {ouvert && (
        <div className="sg-nav-sous-menu" role="menu">
          {membres.map((m) => (
            <Link key={m.path} to={m.path} role="menuitem" className={`sg-nav-sous-lien${estActif(m.path) ? ' sg-nav-sous-actif' : ''}`}
              onClick={() => setOuvert(false)}>
              <m.Ico size={16} aria-hidden="true" />{m.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MenuUtilisateur({ employe, nomAffiche, libelleRole, onDeconnexion }) {
  const [ouvert, setOuvert] = useState(false);
  const ref = useFermetureExterieure(ouvert, () => setOuvert(false));
  const navigate = useNavigate();
  const aller = (chemin) => { setOuvert(false); navigate(chemin); };
  return (
    <div className="sg-utilisateur" ref={ref}>
      <button type="button" className="sg-utilisateur-bouton" aria-haspopup="menu" aria-expanded={ouvert} onClick={() => setOuvert((o) => !o)}
        aria-label={`${nomAffiche}${employe?.matricule ? `, matricule ${employe.matricule}` : ''}, ${libelleRole} — menu du compte`} title={libelleRole}>
        <span className="sg-utilisateur-nom">{nomAffiche}</span>
        {employe?.matricule && <span className="sg-utilisateur-matricule">· {employe.matricule}</span>}
      </button>
      {ouvert && (
        <div className="sg-nav-sous-menu sg-utilisateur-menu" role="menu">
          <div className="sg-utilisateur-entete">
            <div className="sg-utilisateur-nom">{nomAffiche}</div>
            <div className="sg-utilisateur-role">{libelleRole}{employe?.matricule ? ` · ${employe.matricule}` : ''}</div>
          </div>
          {employe?.id && employe?.nature !== 'EXTERNE' && (
            <button type="button" role="menuitem" className="sg-nav-sous-lien" onClick={() => aller(`/employes/${employe.id}`)}>
              <UserRound size={16} aria-hidden="true" />Mon dossier
            </button>
          )}
          <button type="button" role="menuitem" className="sg-nav-sous-lien" onClick={() => aller('/changer-mot-de-passe')}>
            <KeyRound size={16} aria-hidden="true" />Changer mon mot de passe
          </button>
          <div className="sg-nav-separateur" role="presentation" />
          <button type="button" role="menuitem" className="sg-nav-sous-lien sg-nav-danger" onClick={() => { setOuvert(false); onDeconnexion(); }}>
            <LogOut size={16} aria-hidden="true" />Se déconnecter
          </button>
        </div>
      )}
    </div>
  );
}

// `minimal` : en-tête réduit à l'identité et à la déconnexion (changement de mot de passe imposé à
// la première connexion : aucune autre page n'est accessible tant qu'il n'est pas fait).
export default function Layout({ children, minimal = false }) {
  const { employe, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tiroir, setTiroir] = useState(false);

  useEffect(() => { setTiroir(false); }, [location.pathname]);

  const droitsEquipe = useDroitsEquipeMenu(!minimal && !!employe?.id && employe?.nature !== 'EXTERNE');
  const entreeDispositifs = useEntreeDispositifs(!minimal && !!employe?.id && employe?.nature !== 'EXTERNE');
  const items = ajouterEntreeDispositifs(ajouterEntreesEquipe(entreesVisibles(employe), droitsEquipe, employe), entreeDispositifs);
  const blocs = regrouper(items);
  const nomAffiche = [employe?.prenom, (employe?.nom || '').toUpperCase()].filter(Boolean).join(' ') || 'Utilisateur';
  const libelleRole = libelleRoleDe(employe);
  const externe = employe?.nature === 'EXTERNE';
  const deconnexion = () => { logout(); navigate('/login'); };

  // Entrée active : la plus précise qui correspond (/ipm/equipe n'allume pas aussi /ipm).
  const correspond = (base) => location.pathname === base || (location.pathname.startsWith(`${base}/`) && base.length > 1);
  const meilleure = items.map((i) => i.path.split('?')[0]).filter(correspond).sort((a, b) => b.length - a.length)[0];
  const estActif = (path) => path.split('?')[0] === meilleure;
  const accueil = items[0]?.path || '/';

  return (
    <EnteteGlobaleContext.Provider value>
      <div className="sg-app">
        <header className="sg-entete">
          <Link to={accueil} className="sg-marque" aria-label="SOGAS RH — accueil">
            <img src="/tete.png" alt="" className="sg-embleme" />
            <span className="sg-marque-nom">SOGAS RH</span>
          </Link>

          {!minimal && <nav className="sg-nav" aria-label="Navigation principale">
            {blocs.map((b) => (b.lien ? (
              <Link key={b.lien.path} to={b.lien.path} className={`sg-nav-lien${estActif(b.lien.path) ? ' sg-nav-actif' : ''}`}
                aria-current={estActif(b.lien.path) ? 'page' : undefined}>
                {b.lien.label}
              </Link>
            ) : (
              <MenuGroupe key={b.groupe.cle} groupe={b.groupe} membres={b.membres} estActif={estActif}
                actif={b.membres.some((m) => estActif(m.path))} />
            )))}
          </nav>}

          <div className="sg-entete-droite">
            {!minimal && <div className="sg-entete-cloche"><NotificationBell emplacement="entete" couleurIcone="#fff" taille={19} /></div>}
            {minimal
              ? <span className="sg-utilisateur-bouton" style={{ cursor: 'default' }}><span className="sg-utilisateur-nom">{nomAffiche}</span>{employe?.matricule && <span className="sg-utilisateur-matricule">· {employe.matricule}</span>}</span>
              : <MenuUtilisateur employe={employe} nomAffiche={nomAffiche} libelleRole={libelleRole} onDeconnexion={deconnexion} />}
            <button type="button" className="sg-entete-icone sg-prise" onClick={deconnexion} aria-label="Se déconnecter" title="Se déconnecter">
              <Unplug size={20} aria-hidden="true" />
            </button>
            {!minimal && <button type="button" className="sg-entete-icone sg-entete-menu-mobile" onClick={() => setTiroir(true)} aria-label="Ouvrir le menu" title="Menu">
              <Menu size={21} aria-hidden="true" />
            </button>}
          </div>
        </header>

        {tiroir && <div className="sg-tiroir-voile" onClick={() => setTiroir(false)} aria-hidden="true" />}
        <aside className={`sg-tiroir${tiroir ? ' sg-tiroir-ouvert' : ''}`} aria-label="Menu" aria-hidden={!tiroir}>
          <div className="sg-tiroir-entete">
            <span className="sg-tiroir-identite">
              <span className="sg-utilisateur-nom">{nomAffiche}</span>
              <span className="sg-utilisateur-role">{libelleRole}{employe?.matricule ? ` · ${employe.matricule}` : ''}</span>
            </span>
            <button type="button" className="sg-entete-icone sg-tiroir-fermer" onClick={() => setTiroir(false)} aria-label="Fermer le menu">
              <X size={20} aria-hidden="true" />
            </button>
          </div>
          <nav className="sg-tiroir-nav">
            {blocs.map((b) => (b.lien ? (
              <Link key={b.lien.path} to={b.lien.path} className={`sg-tiroir-lien${estActif(b.lien.path) ? ' sg-tiroir-actif' : ''}`}>
                <b.lien.Ico size={18} aria-hidden="true" />{b.lien.label}
              </Link>
            ) : (
              <div key={b.groupe.cle} className="sg-tiroir-groupe">
                <div className="sg-tiroir-groupe-titre">{b.groupe.libelle}</div>
                {b.membres.map((m) => (
                  <Link key={m.path} to={m.path} className={`sg-tiroir-lien${estActif(m.path) ? ' sg-tiroir-actif' : ''}`}>
                    <m.Ico size={18} aria-hidden="true" />{m.label}
                  </Link>
                ))}
              </div>
            )))}
          </nav>
          <div className="sg-tiroir-pied">
            {employe?.id && !externe && (
              <Link to={`/employes/${employe.id}`} className="sg-tiroir-lien"><UserRound size={18} aria-hidden="true" />Mon dossier</Link>
            )}
            <Link to="/changer-mot-de-passe" className="sg-tiroir-lien"><KeyRound size={18} aria-hidden="true" />Changer mon mot de passe</Link>
            <button type="button" className="sg-tiroir-lien sg-nav-danger" onClick={deconnexion}>
              <LogOut size={18} aria-hidden="true" />Se déconnecter
            </button>
          </div>
        </aside>

        <main className="sg-contenu">{children}</main>
      </div>
    </EnteteGlobaleContext.Provider>
  );
}
